import { useState, useEffect } from "react";
import { billingFetch } from "./billingApi";
import "./UsageDetails.css";

const MODE_CONFIG = [
  { key: "simple", label: "Simple", desc: "Quick tasks & daily questions", icon: "bi-lightning-fill", color: "#16a34a", patterns: ["haiku", "gpt-4o-mini", "gpt-5-mini", "gpt-5-nano", "gpt-4.1-mini", "gpt-4.1-nano", "gemini-2.0-flash", "gemini-2.5-flash"] },
  { key: "smart", label: "Smart", desc: "Complex tasks & multi-step actions", icon: "bi-cpu-fill", color: "#7c3aed", patterns: ["sonnet", "gpt-4o", "gpt-5", "gpt-4.1", "gpt-5.4", "gemini-2.5-pro"] },
  { key: "genius", label: "Genius", desc: "Deep analysis & research", icon: "bi-stars", color: "#dc2626", patterns: ["opus"] },
  { key: "voice", label: "Voice", desc: "Voice conversations & transcription", icon: "bi-mic-fill", color: "#2563eb", patterns: ["realtime", "whisper", "tts"] },
];

function modelToMode(model) {
  const m = (model || "").toLowerCase();
  // Check simple first (has longer patterns like gpt-4o-mini that must match before gpt-4o)
  for (const mode of MODE_CONFIG) {
    for (const p of mode.patterns) {
      if (m.includes(p)) return mode.key;
    }
  }
  return "other";
}

export default function UsageDetails({ userId, isDarkMode }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!userId) return;
    fetchUsage();
  }, [userId]);

  async function fetchUsage() {
    setLoading(true);
    setError(null);
    try {
      const res = await billingFetch("/stripe/usage-details");
      setData(res);
    } catch (err) {
      setError("Failed to load usage details.");
      console.error("[UsageDetails]", err);
    } finally {
      setLoading(false);
    }
  }

  if (loading) {
    return (
      <div className={`usage-details ${isDarkMode ? "dark" : ""}`}>
        <h2>Credit Usage</h2>
        <div className="ud-skeleton">
          <div className="ud-skeleton-row" />
          <div className="ud-skeleton-row short" />
          <div className="ud-skeleton-row" />
          <div className="ud-skeleton-row short" />
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className={`usage-details ${isDarkMode ? "dark" : ""}`}>
        <h2>Credit Usage</h2>
        <p className="ud-error">{error}</p>
        <button className="ud-retry-btn" onClick={fetchUsage}>Retry</button>
      </div>
    );
  }

  const credits = data?.credits || {};
  const totalRequests = data?.requests ?? 0;
  const breakdown = Array.isArray(credits.breakdown) ? credits.breakdown : [];

  // Aggregate credits by mode
  const modeCredits = { simple: 0, smart: 0, genius: 0, voice: 0, other: 0 };
  for (const row of breakdown) {
    const mode = modelToMode(row.model);
    modeCredits[mode] += row.credits ?? 0;
  }
  const breakdownTotal = Object.values(modeCredits).reduce((a, b) => a + b, 0);

  return (
    <div className={`usage-details ${isDarkMode ? "dark" : ""}`}>
      <h2>Credit Usage</h2>

      {/* Summary cards */}
      <div className="ud-summary">
        <div className="ud-card">
          <div className="ud-card-label">Total Credits</div>
          <div className="ud-card-value">{Math.round(credits.total ?? 0).toLocaleString()}</div>
        </div>
        <div className="ud-card">
          <div className="ud-card-label">Used</div>
          <div className="ud-card-value ud-used">{Math.round(credits.used ?? 0).toLocaleString()}</div>
        </div>
        <div className="ud-card">
          <div className="ud-card-label">Remaining</div>
          <div className="ud-card-value ud-remaining">{Math.round(credits.remaining ?? 0).toLocaleString()}</div>
        </div>
        <div className="ud-card">
          <div className="ud-card-label">API Requests</div>
          <div className="ud-card-value">{totalRequests.toLocaleString()}</div>
        </div>
      </div>

      {/* Usage bar */}
      {credits.total > 0 && (
        <div className="ud-bar-wrap">
          <div className="ud-bar">
            <div
              className="ud-bar-fill"
              style={{ width: `${Math.min(100, ((credits.used ?? 0) / credits.total) * 100)}%` }}
            />
          </div>
          <div className="ud-bar-labels">
            <span>{Math.round(((credits.used ?? 0) / credits.total) * 100)}% used</span>
            <span>{Math.round(credits.remaining ?? 0).toLocaleString()} left</span>
          </div>
        </div>
      )}

      {/* Mode-wise usage */}
      <h3 className="ud-section-title">Usage by Mode</h3>
      {breakdown.length > 0 ? (
        <div className="ud-modes">
          {MODE_CONFIG.map((mode) => {
            const used = modeCredits[mode.key] ?? 0;
            const pct = breakdownTotal > 0 ? Math.round((used / breakdownTotal) * 100) : 0;
            return (
              <div key={mode.key} className="ud-mode-card">
                <div className="ud-mode-icon" style={{ background: mode.color }}>
                  <i className={`bi ${mode.icon}`} />
                </div>
                <div className="ud-mode-info">
                  <div className="ud-mode-label">{mode.label}</div>
                  <div className="ud-mode-desc">{mode.desc}</div>
                </div>
                <div className="ud-mode-stats">
                  <div className="ud-mode-credits">{used.toFixed(1)}</div>
                  <div className="ud-mode-pct">{pct}% of usage</div>
                </div>
              </div>
            );
          })}
          {modeCredits.other > 0 && (
            <div className="ud-mode-card">
              <div className="ud-mode-icon" style={{ background: "#888" }}>
                <i className="bi bi-three-dots" />
              </div>
              <div className="ud-mode-info">
                <div className="ud-mode-label">Other</div>
                <div className="ud-mode-desc">Background tasks & system operations</div>
              </div>
              <div className="ud-mode-stats">
                <div className="ud-mode-credits">{modeCredits.other.toFixed(1)}</div>
                <div className="ud-mode-pct">{breakdownTotal > 0 ? Math.round((modeCredits.other / breakdownTotal) * 100) : 0}% of usage</div>
              </div>
            </div>
          )}
        </div>
      ) : (
        <div className="ud-empty">
          <p>No usage data yet. Start chatting with Zoomey to see your credit breakdown here.</p>
        </div>
      )}
    </div>
  );
}
